import {SellerType} from '@sollapay/enums';
import {buildIdentityKey} from '@sollapay/utils';

import {diffDeclaredIdentity} from './declared-identity.rules';
import {isPassportIdentity} from './identity-document.rules';

import type {DeclaredIdentity, DeclaredIdentityField} from './declared-identity.rules';
import type {IdentityDocumentType} from '@sollapay/enums';

/** The fields that decide whether two selling parties are the same entity. */
export interface SellerIdentity {
  sellerType?: SellerType | null;
  idNumber?: string | null;
  idType?: IdentityDocumentType | null;
  idCountry?: string | null;
}

/** A missing sellerType reads as "not a corporation", matching every other call site. */
export function isForeignPassportIdentity(identity: SellerIdentity): boolean {
  return isPassportIdentity(identity.idType) && identity.sellerType !== SellerType.CORPORATION;
}

/**
 * Null when no number is filled in yet — a draft row identifies nobody. Only a foreign passport is
 * scoped by issuing country; company numbers and Israeli national IDs share one country-less
 * namespace, so a country left over from a passport selection cannot split a key that should collide.
 */
export function resolveSellerIdentityKey(identity: SellerIdentity): string | null {
  return buildIdentityKey({
    idNumber: identity.idNumber,
    isPassport: isForeignPassportIdentity(identity),
    idCountry: identity.idCountry
  });
}

/** Indexes of the entries repeating an identity seen earlier in the list — the first is left clean. */
export function findDuplicateSellerIdentityIndexes(identities: SellerIdentity[]): number[] {
  const seen = new Set<string>();
  const duplicates: number[] = [];

  identities.forEach((identity, index) => {
    const key = resolveSellerIdentityKey(identity);
    if (!key) return;
    if (seen.has(key)) duplicates.push(index);
    seen.add(key);
  });

  return duplicates;
}

/** Merges a partial incoming entry with its stored row — autosave omits fields left unchanged, so an
 * omitted field inherits the persisted value rather than reading as "cleared". */
export function resolvePartialSellerIdentity(
  entry: SellerIdentity,
  stored: SellerIdentity | undefined
): SellerIdentity {
  // An explicit null must beat the stored value, so `??` cannot express this.
  const resolve = <T>(incoming: T | undefined, persisted: T | null | undefined) =>
    incoming === undefined ? persisted : incoming;

  return {
    sellerType: resolve(entry.sellerType, stored?.sellerType),
    idNumber: resolve(entry.idNumber, stored?.idNumber),
    idType: resolve(entry.idType, stored?.idType),
    idCountry: resolve(entry.idCountry, stored?.idCountry)
  };
}

/** A seller's stored or submitted fields, under the names a seller row uses. */
export interface SellerDeclaredFields {
  fullName?: string | null;
  idNumber?: string | null;
  idType?: string | null;
  idCountry?: string | null;
  birthOrIncorporationDate?: string | null;
  address?: string | null;
  sellerRole?: string | null;
}

function toDeclaredIdentity(seller: SellerDeclaredFields): DeclaredIdentity {
  return {
    name: seller.fullName,
    role: seller.sellerRole,
    idNumber: seller.idNumber,
    idType: seller.idType,
    idCountry: seller.idCountry,
    birthOrIncorporationDate: seller.birthOrIncorporationDate,
    address: seller.address
  };
}

/**
 * Which declared fields an incoming seller entry changes. A row with no stored counterpart is new;
 * a save that only touches other data (acquaintance, tax, PEP) changes nothing, so a signed
 * declaration stands.
 */
export function diffSellerIdentity(
  incoming: SellerDeclaredFields,
  stored: SellerDeclaredFields | undefined
): DeclaredIdentityField[] {
  return diffDeclaredIdentity(stored && toDeclaredIdentity(stored), toDeclaredIdentity(incoming));
}
